import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { DataOutput } from '../models/data-output.model';
import { DashboardData, DataResult, Metadata } from '../models/data-result.model';
import { InputForm } from '../models/input-form.model';
import { NotificationService } from '../shared/notification/notification.service';

@Injectable({
  providedIn: 'root',
})
export class OfferService {
  private readonly dashboardDataSubject = new BehaviorSubject<DashboardData>({ inputValue: [], outputValue: { sections: [], outputs: {} } });

  private _resultForm?: any;
  public get resultForm(): any {
    return this._resultForm;
  }

  private _inputForm?: InputForm[];
  public get inputForm(): InputForm[] | undefined {
    return this._inputForm;
  }

  private output?: DataOutput;

  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly notificationService = inject(NotificationService);

  dashboardData$(quoteId: string): Observable<DashboardData> {
    if (!quoteId || (this.output && this.inputForm)) {
      return this.dashboardDataSubject.asObservable();
    } else {
      return this.getQuoteData(quoteId).pipe(switchMap(() => this.dashboardDataSubject.asObservable()));
    }
  }

  getOfferDetails(): Observable<{ [key: string]: any[] }> {
    return this.http.get<any[]>(`${environment.baseUrl}/templates/list`).pipe(
      map((data) => {
        return data.reduce((res, item) => {
          const existing = res[item.typology] || [];
          return {
            ...res,
            [item.typology]: [...existing, item],
          };
        }, {} as { [key: string]: any[] });
      })
    );
  }

  cloneTemplate(templateDriveObject: any) {
    return this.http.post<Metadata>(`${environment.baseUrl}/templates/clone`, { ...templateDriveObject });
  }

  getFormInput(templateDriveId) {
    return this.http.get(`${environment.baseUrl}/templates/input/${templateDriveId}`).pipe(tap((inputForm: InputForm[]) => (this._inputForm = inputForm)));
  }

  processOutput(inputForm: any): Observable<DataOutput> {
    this._resultForm = inputForm.elements;
    return this.http.post<any>(`${environment.baseUrl}/quotes/process`, { ...inputForm }).pipe(
      map((resp) => {
        const rawOutputs = resp.data || [];
        const dataOutputs: DataOutput = {
          ...this.elaborateOutputData(rawOutputs),
          metadata: resp.metadata,
        };
        this.output = dataOutputs;
        this.processDashboardData();
        return dataOutputs;
      })
    );
  }

  elaborateOutputData(rawOutputs: any[]) {
    const outputs = rawOutputs.reduce((acc, item) => {
      const sectionKey = item.tabella?.trim();
      return {
        ...acc,
        [sectionKey]: [...(acc[sectionKey] || []), item],
      };
    }, {});

    const dataOutputs: DataOutput = {
      outputs,
      sections: Object.keys(outputs),
    };
    return dataOutputs;
  }

  getQuoteData(quoteId: string) {
    return this.http.get(`${environment.baseUrl}/quotes/${quoteId}`).pipe(
      map((resultData: any) => {
        const quoteData: DataResult = {
          ...resultData,
          output: this.elaborateOutputData(resultData.output),
        };
        return quoteData;
      }),
      tap((resultData: DataResult) => {
        this._resultForm = resultData.resultForm;
        this._inputForm = resultData.inputForm;
        this.output = resultData.output;
        this.processDashboardData();
      })
    );
  }

  getQuoteMetadata(quoteId: string) {
    if (!quoteId) {
      return of(null);
    }
    return this.http.get<Metadata>(`${environment.baseUrl}/quotes/metadata/${quoteId}`).pipe(
      catchError((error: HttpErrorResponse) => {
        this.showErrorMessage(`Il preventivo non risulta a sistema, verrai reindirizzato per procedere con una nuova compilazione`);
        return throwError(() => new Error(error.message));
      })
    );
  }

  getDriveIdData(templateDriveId: string) {
    return this.http.get(`${environment.baseUrl}/templates/list/${templateDriveId}`).pipe(
      catchError((error: HttpErrorResponse) => {
        this.showErrorMessage(`Il template non risulta a sistema, verrai reindirizzato per procedere con una nuova compilazione`);
        return throwError(() => new Error(error.message));
      })
    );
  }

  getPdf(quoteId: string) {
    const httpOptions = {
      responseType: 'blob' as 'json',
    };

    return this.http.get(`${environment.baseUrl}/download-document/${quoteId}`, httpOptions);
  }

  private processDashboardData() {
    const inputValue = this.inputForm?.map(({ key, description: label, parameterType: type }) => ({
      label,
      type,
      value: this.resultForm[key],
    }));
    this.dashboardDataSubject.next({ inputValue, outputValue: this.output });
  }

  showErrorMessage(message: string) {
    const inputs = {
      message,
      nextBtn: 'Prosegui',
      cancelBtn: '',
      classConfirmBtn: 'btn-primary',
      classCancelBtn: 'd-none',
    };
    this.notificationService.openWarningUser(inputs).finally(() => this.router.navigate(['/']));
  }
}
